// ABOUTME: The quiet actions under an agent's message (Copy, Read aloud), the way ChatGPT shows
// them: small icon buttons in one row, each with a 44px touch area on a phone.
import { Loader2, Square, Volume2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

/** One row of message actions; spaced on a phone so neighbouring touch areas never overlap. */
export function MessageActions({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn('flex items-center gap-0.5 pointer-coarse:gap-4', className)}
      data-testid="message-actions"
    >
      {children}
    </div>
  )
}

export function MessageActionButton({
  label,
  tooltip,
  testId,
  onClick,
  active,
  children,
}: {
  label: string
  tooltip: string
  testId: string
  onClick: () => void
  /** Keeps the button lit while its action runs (reading aloud). */
  active?: boolean
  children: React.ReactNode
}) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          data-active={active || undefined}
          data-testid={testId}
          className="text-muted-foreground hover:text-foreground hover:bg-muted data-[active]:text-foreground relative flex size-7 shrink-0 items-center justify-center rounded-md transition-colors pointer-coarse:before:absolute pointer-coarse:before:-inset-2 [&_svg]:size-3.5"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

/** Read the message aloud; while it loads or plays, the same button stops it. */
export function ReadAloudButton({
  isSpeaking,
  isLoading,
  onPlay,
  onStop,
}: {
  isSpeaking: boolean
  isLoading: boolean
  onPlay: () => void
  onStop: () => void
}) {
  const active = isSpeaking || isLoading
  return (
    <MessageActionButton
      label={active ? 'Stop reading' : 'Read aloud'}
      tooltip={active ? 'Stop' : 'Read aloud'}
      testId="read-aloud-button"
      onClick={active ? onStop : onPlay}
      active={active}
    >
      {isLoading ? (
        <Loader2 className="animate-spin" />
      ) : isSpeaking ? (
        <Square className="fill-current" />
      ) : (
        <Volume2 />
      )}
    </MessageActionButton>
  )
}
